/**
 * Neogen PDP - Purchase Toggle & Interactions
 * Handles one-time vs subscription toggle behavior
 * Prepared for future Loop Subscriptions integration
 */

(function() {
  'use strict';

  /**
   * Purchase Toggle Component
   * Toggles between one-time purchase and subscription options
   */
  class NeogenPurchaseToggle {
    constructor(container) {
      this.container = container;
      this.buttons = container.querySelectorAll('.neogen-purchase-toggle__option');
      this.notes = document.querySelectorAll('[data-neogen-purchase-note]');
      this.priceElements = document.querySelectorAll('[data-neogen-price]');

      // Store original prices for calculations
      this.originalPrices = {};
      this.discountPercent = parseFloat(container.dataset.subscriptionDiscount) || 10;

      this.init();
    }

    init() {
      // Store original prices
      this.priceElements.forEach((el, index) => {
        const priceText = el.textContent.trim();
        const priceValue = this.parsePriceValue(priceText);
        this.originalPrices[index] = {
          element: el,
          value: priceValue,
          formatted: priceText
        };
      });

      // Bind click events
      this.buttons.forEach(button => {
        button.addEventListener('click', (e) => this.handleToggle(e));
      });

      // Set initial state
      const activeButton = container.querySelector('.neogen-purchase-toggle__option.is-active');
      if (activeButton) {
        this.updateState(activeButton.dataset.mode || 'onetime');
      }
    }

    handleToggle(event) {
      const button = event.currentTarget;
      const mode = button.dataset.mode || 'onetime';

      // Update active states
      this.buttons.forEach(btn => btn.classList.remove('is-active'));
      button.classList.add('is-active');

      // Update UI
      this.updateState(mode);

      // Dispatch custom event for external listeners (Loop, analytics, etc.)
      this.container.dispatchEvent(new CustomEvent('neogen:purchase-mode-change', {
        bubbles: true,
        detail: { mode }
      }));
    }

    updateState(mode) {
      // Update notes visibility
      this.notes.forEach(note => {
        const noteMode = note.dataset.neogenPurchaseNote;
        note.hidden = noteMode !== mode;
      });

      // Update prices if subscription mode
      if (mode === 'subscribe') {
        this.applySubscriptionPrices();
      } else {
        this.restoreOriginalPrices();
      }

      // TODO: When Loop is integrated, call their API here
      // if (window.Loop && typeof window.Loop.setSellingPlan === 'function') {
      //   if (mode === 'subscribe') {
      //     window.Loop.setSellingPlan(this.container.dataset.sellingPlanId);
      //   } else {
      //     window.Loop.clearSellingPlan();
      //   }
      // }
    }

    applySubscriptionPrices() {
      Object.values(this.originalPrices).forEach(priceData => {
        if (priceData.value) {
          const discountedValue = priceData.value * (1 - this.discountPercent / 100);
          priceData.element.textContent = this.formatPrice(discountedValue);
        }
      });
    }

    restoreOriginalPrices() {
      Object.values(this.originalPrices).forEach(priceData => {
        priceData.element.textContent = priceData.formatted;
      });
    }

    parsePriceValue(priceString) {
      // Handle various currency formats
      const cleaned = priceString.replace(/[^\d.,]/g, '');
      // Handle comma as decimal separator (European format)
      const normalized = cleaned.replace(',', '.');
      return parseFloat(normalized) || null;
    }

    formatPrice(value) {
      // Get currency format from Shopify's money format or default to USD
      const formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      });
      return formatter.format(value);
    }
  }

  /**
   * Active Ingredients Overlay Enhancement
   * Adds keyboard navigation and escape to close
   */
  class NeogenActivesOverlay {
    constructor() {
      this.overlays = document.querySelectorAll('.neogen-actives-overlay');
      this.init();
    }

    init() {
      // Add escape key listener
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          this.closeAllOverlays();
        }
      });

      // Close when clicking outside the panel
      this.overlays.forEach(overlay => {
        overlay.addEventListener('click', (e) => {
          if (e.target === overlay) {
            this.closeOverlay(overlay);
          }
        });
      });
    }

    closeAllOverlays() {
      // Remove hash to close :target overlay
      if (window.location.hash.includes('neogen-actives')) {
        history.pushState('', document.title, window.location.pathname + window.location.search);
      }
    }

    closeOverlay(overlay) {
      if (overlay.matches(':target')) {
        history.pushState('', document.title, window.location.pathname + window.location.search);
      }
    }
  }

  /**
   * Intersection Observer for animations
   * Triggers animations when sections come into view
   */
  class NeogenAnimations {
    constructor() {
      this.animatedElements = document.querySelectorAll(
        '.neogen-rtb__card.js-animation-fade-in, ' +
        '.neogen-texture, ' +
        '.neogen-usage, ' +
        '.neogen-ritual'
      );

      if (this.animatedElements.length === 0) return;

      // Respect reduced motion preference
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.animatedElements.forEach(el => el.classList.add('is-visible'));
        return;
      }

      this.init();
    }

    init() {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-visible');
              // Optionally unobserve after animation
              // observer.unobserve(entry.target);
            }
          });
        },
        {
          threshold: 0.15,
          rootMargin: '0px 0px -50px 0px'
        }
      );

      this.animatedElements.forEach(el => observer.observe(el));
    }
  }

  /**
   * Initialize all components on DOM ready
   */
  function init() {
    // Initialize purchase toggles
    document.querySelectorAll('[data-neogen-purchase-toggle]').forEach(container => {
      new NeogenPurchaseToggle(container);
    });

    // Initialize overlay enhancements
    new NeogenActivesOverlay();

    // Initialize animations
    new NeogenAnimations();
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-initialize on Shopify section load (for theme editor)
  document.addEventListener('shopify:section:load', init);
})();
